import { INavData } from '@coreui/angular-pro';
import { BackofficePermission } from '@core-interfaces/global/permissions';

/**
 * Permiso requerido para ver cada entrada del menú (por URL).
 * Las entradas no listadas aquí (Dashboard, Prestadores, Clientes, etc.) se
 * muestran a cualquier admin autenticado.
 */
export const navPermissions: Record<string, string | string[]> = {
  '/categorias': BackofficePermission.CATALOG_VIEW_CATEGORIES,
  '/servicios': BackofficePermission.CATALOG_VIEW_SERVICES,
  '/ride-modalities': BackofficePermission.RIDE_MODALITIES_VIEW,
  '/catalogo-vehiculos': BackofficePermission.VEHICLE_CATALOG_VIEW,
  '/banners': BackofficePermission.BANNERS_VIEW,
  '/cupones': BackofficePermission.COUPONS_VIEW,
  '/notificaciones-push': BackofficePermission.PUSH_NOTIFICATIONS_VIEW,
  '/correo-electronico': BackofficePermission.EMAIL_VIEW_LOGS,
  '/tickets/motivos': BackofficePermission.TICKETS_VIEW_REASONS,
  '/liquidaciones': BackofficePermission.SETTLEMENTS_VIEW,
  '/retiros': BackofficePermission.WITHDRAWALS_VIEW,
  '/usuarios/administradores': BackofficePermission.USERS_VIEW,
  '/usuarios/postulaciones': BackofficePermission.APPLICATIONS_VIEW,
  '/roles': BackofficePermission.ROLES_VIEW,
  '/configuraciones': BackofficePermission.SYSTEM_CONFIGS_VIEW,
  '/mensajes': BackofficePermission.MESSAGES_VIEW,
};

/**
 * Filtra el menú según los permisos del usuario: oculta ítems sin permiso,
 * padres cuyos hijos quedaron todos ocultos y títulos de sección sin ítems.
 */
export function filterNavByPermissions(
  items: INavData[],
  can: (permission: string | string[]) => boolean,
): INavData[] {
  const visible: INavData[] = [];

  for (const item of items) {
    if (item.title) {
      visible.push(item);
      continue;
    }

    const required = item.url ? navPermissions[item.url as string] : undefined;
    if (required && !can(required)) {
      continue;
    }

    if (item.children) {
      const children = filterNavByPermissions(item.children, can);
      if (children.length === 0) {
        continue;
      }
      visible.push({ ...item, children });
      continue;
    }

    visible.push(item);
  }

  return dropEmptySections(visible);
}

/** Elimina los títulos de sección que no van seguidos de al menos un ítem. */
function dropEmptySections(items: INavData[]): INavData[] {
  return items.filter((item, index) => {
    if (!item.title) {
      return true;
    }
    const next = items[index + 1];
    return !!next && !next.title;
  });
}

export const navItems: INavData[] = [
  {
    name: 'Dashboard',
    url: '/dashboard',
    iconComponent: { name: 'cil-speedometer' },
  },
  {
    title: true,
    name: 'Gestión de servicios'
  },
  {
    name: 'Categorías',
    url: '/categorias',
    iconComponent: { name: 'cil-widgets' }
  },
  {
    name: 'Servicios',
    url: '/servicios',
    iconComponent: { name: 'cil-handshake' }
  },
  {
    title: true,
    name: 'ViajaYa'
  },
  {
    name: 'Modalidades de Viaje',
    url: '/ride-modalities',
    iconComponent: { name: 'cil-car-alt' }
  },
  {
    name: 'Catálogo de Vehículos',
    url: '/catalogo-vehiculos',
    iconComponent: { name: 'cil-car-alt' },
    children: [
      {
        name: 'Marcas',
        url: '/catalogo-vehiculos/marcas',
      },
      {
        name: 'Modelos',
        url: '/catalogo-vehiculos/modelos',
      },
    ],
  },
  {
    title: true,
    name: 'Marketing y Comunicación'
  },
  {
    name: 'Banners',
    url: '/banners',
    iconComponent: { name: 'cil-image' }
  },
  // {
  //   name: 'Promociones',
  //   url: '/promociones',
  //   iconComponent: { name: 'cil-gift' }
  // },
  {
    name: 'Cupones',
    url: '/cupones',
    iconComponent: { name: 'cil-tags' }
  },
  {
    name: 'Notificaciones Push',
    url: '/notificaciones-push',
    iconComponent: { name: 'cil-bell' }
  },
  {
    name: 'Correo Electrónico',
    url: '/correo-electronico',
    iconComponent: { name: 'cil-envelope-closed' }
  },
  {
    title: true,
    name: 'Tickets e Incidencias'
  },
  {
    name: 'Gestión de Tickets',
    url: '/tickets/gestion',
    iconComponent: { name: 'cilList' }
  },
  {
    name: 'Motivos de Ticket',
    url: '/tickets/motivos',
    iconComponent: { name: 'cil-description' }
  },
  {
    name: 'Solicitudes SOS',
    url: '/sos',
    iconComponent: { name: 'cil-warning' }
  },
  {
    title: true,
    name: 'Finanzas'
  },
  {
    name: 'Liquidaciones',
    url: '/liquidaciones',
    iconComponent: { name: 'cil-dollar' }
  },
  {
    name: 'Retiros',
    url: '/retiros',
    iconComponent: { name: 'cil-credit-card' }
  },
  {
    title: true,
    name: 'Administración'
  },
  {
    name: 'Prestadores',
    url: '/usuarios/prestadores',
    iconComponent: { name: 'cil-people' }
  },
  {
    name: 'Postulaciones',
    url: '/usuarios/postulaciones',
    iconComponent: { name: 'cil-description' }
  },
  {
    name: 'Clientes',
    url: '/usuarios/clientes',
    iconComponent: { name: 'cil-people' }
  },
  {
    name: 'Pedidos',
    url: '/pedidos/listado',
    iconComponent: { name: 'cilList' }
  },
  {
    name: 'Administradores',
    url: '/usuarios/administradores',
    iconComponent: { name: 'cil-user' }
  },
  {
    name: 'Roles y permisos',
    url: '/roles',
    iconComponent: { name: 'cil-lock-locked' }
  },
  {
    name: 'Mensajes',
    url: '/mensajes',
    iconComponent: { name: 'cilSpeech' }
  },
  {
    name: 'Configuración del sistema',
    url: '/configuraciones',
    iconComponent: { name: 'cilSettings' }
  },
];
